const CURRENCY_FORMATTER = new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
});

const CREDIT_TYPE_CONFIG = [
  { key: 'vehicular', label: 'Crédito vehicular', order: 1, match: (value) => value.includes('vehicular') || value.includes('vehiculo') },
  { key: 'prestamo', label: 'Préstamo', order: 2, match: (value) => value.includes('prestamo') },
  { key: 'tarjeta', label: 'Tarjeta', order: 3, match: (value) => value.includes('tarjeta') },
  { key: 'hipoteca', label: 'Hipoteca', order: 4, match: (value) => value.includes('hipoteca') },
  { key: 'otros', label: 'Otros', order: 5, match: (value) => value.includes('otro') },
];

const normalizeText = (value = '') =>
  String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const CREDIT_SITUATION_ALIASES = {
  'al corriente': 'Al corriente de pago',
  'al corriente de pago': 'Al corriente de pago',
  'en impago': 'Impago',
  'impago': 'Impago',
  'atraso hasta 3 meses': 'Atraso hasta 3 meses',
  'atraso superior a 3 meses': 'Atraso superior a 3 meses',
};

export const normalizeCreditSituation = (situacion = '') => {
  const rawValue = String(situacion || '').trim();

  if (!rawValue) {
    return '';
  }

  return CREDIT_SITUATION_ALIASES[normalizeText(rawValue)] || rawValue;
};

export const getCreditSituationColor = (situacion = '') => {
  switch (normalizeCreditSituation(situacion)) {
    case 'Al corriente de pago':
      return 'success';
    case 'Atraso hasta 3 meses':
      return 'warning';
    case 'Atraso superior a 3 meses':
    case 'Impago':
      return 'error';
    default:
      return 'default';
  }
};

export const calcAge = (dateStr) => {
  if (!dateStr) return null;

  const birthDate = new Date(dateStr);
  if (Number.isNaN(birthDate.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }

  return age;
};

export const parseNumericValue = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

  const sanitized = String(value)
    .trim()
    .replace(/\s/g, '')
    .replace(/[^\d,.-]/g, '');

  if (!sanitized) return 0;

  const lastCommaIndex = sanitized.lastIndexOf(',');
  const lastDotIndex = sanitized.lastIndexOf('.');

  if (lastCommaIndex !== -1 && lastDotIndex !== -1) {
    const decimalIndex = Math.max(lastCommaIndex, lastDotIndex);
    const integerPart = sanitized.slice(0, decimalIndex).replace(/[.,]/g, '');
    const fractionalPart = sanitized.slice(decimalIndex + 1).replace(/[.,]/g, '');
    const parsed = Number.parseFloat(`${integerPart}.${fractionalPart}`);

    return Number.isFinite(parsed) ? parsed : 0;
  }

  if (lastCommaIndex !== -1 || lastDotIndex !== -1) {
    const separator = lastCommaIndex !== -1 ? ',' : '.';
    const parts = sanitized.split(separator);

    if (parts.length === 2) {
      const [, decimals] = parts;
      const normalizedValue = decimals.length <= 2
        ? `${parts[0].replace(/[.,]/g, '')}.${decimals}`
        : sanitized.replace(/[.,]/g, '');
      const parsed = Number.parseFloat(normalizedValue);

      return Number.isFinite(parsed) ? parsed : 0;
    }
  }

  const parsed = Number.parseFloat(sanitized.replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

export const formatCurrency = (value) => CURRENCY_FORMATTER.format(parseNumericValue(value));

export const getDisplayAge = (holderData) => {
  const storedAge = parseNumericValue(holderData?.edad);
  if (storedAge > 0) {
    return Math.trunc(storedAge);
  }

  return calcAge(holderData?.fechaNacimiento);
};

const getCreditTypeDescriptor = (tipoCredito) => {
  const normalizedType = normalizeText(tipoCredito);

  const matchedType = CREDIT_TYPE_CONFIG.find(({ match }) => match(normalizedType));
  if (matchedType) {
    return matchedType;
  }

  if (!normalizedType) {
    return {
      key: 'sin-tipo',
      label: 'Sin tipo',
      order: 99,
    };
  }

  return {
    key: `custom-${normalizedType.replace(/\s+/g, '-')}`,
    label: String(tipoCredito).trim(),
    order: 98,
  };
};

export const getCreditTypeBreakdown = (creditos = []) => {
  const groupedCredits = new Map();

  creditos.forEach((credit) => {
    const descriptor = getCreditTypeDescriptor(credit?.tipoCredito);
    const currentSummary = groupedCredits.get(descriptor.key) || {
      ...descriptor,
      total: 0,
      count: 0,
    };

    currentSummary.total += parseNumericValue(credit?.capitalPendiente);
    currentSummary.count += 1;
    currentSummary.highlightInRed = descriptor.key === 'tarjeta' && currentSummary.total >= 12000;

    groupedCredits.set(descriptor.key, currentSummary);
  });

  return Array.from(groupedCredits.values()).sort((left, right) => {
    if (left.order !== right.order) {
      return left.order - right.order;
    }

    return left.label.localeCompare(right.label, 'es');
  });
};
